import { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Paper,
    Stack,
    Typography,
    IconButton,
    Chip,
    Menu,
    MenuItem,
    Tooltip,
    Slider,
    Autocomplete,
    TextField,
    Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Iconify from 'src/components/iconify';

// Styled components for Designation Hierarchy Tree (Org Chart visual lines)
const StyledNodeContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    paddingTop: 20,
}));

const StyledNodeLineTop = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: '50%',
    width: 2,
    height: 20,
    backgroundColor: theme.palette.divider,
    transform: 'translateX(-50%)',
}));

const StyledNodeLineBottom = styled(Box)(({ theme }) => ({
    width: 2,
    height: 20,
    backgroundColor: theme.palette.divider,
}));

const StyledChildrenContainer = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: 0,
    position: 'relative',
}));

const StyledConnectorLine = styled(Box)(({ theme, first, last }) => ({
    position: 'absolute',
    top: 0,
    left: first ? '50%' : 0,
    right: last ? '50%' : 0,
    height: 2,
    backgroundColor: theme.palette.divider,
}));

/**
 * Recursive Designation Chart Node
 */
function DesignationOrgChartNode({
    node,
    designations,
    onRemoveNode,
    onRemoveBranch,
    onAddChild,
    onDesignationChange,
    onManageStaff,
    level = 0,
    zoom = 1
}) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleRemoveNode = () => {
        onRemoveNode(node.id);
        handleMenuClose();
    };

    const handleRemoveBranch = () => {
        onRemoveBranch(node.id);
        handleMenuClose();
    };

    const handleAddChild = () => {
        onAddChild(node.id);
        handleMenuClose();
    };

    const hasChildren = node.children && node.children.length > 0;

    // Node styling
    const getNodeBgColor = () => {
        if (node.role === 'leader') return 'primary.lighter';
        return 'background.paper';
    };

    const getNodeBorderColor = () => {
        if (node.role === 'leader') return 'primary.main';
        return 'divider';
    };

    const getNodeBorderWidth = () => {
        if (node.role === 'leader') return 2;
        return 1;
    };

    // Zoom-driven sizing
    const cardW = Math.max(220, Math.round(280 * zoom));
    const nodePad = Math.max(12, Math.round(20 * zoom));

    return (
        <StyledNodeContainer>
            {/* Top Connector Line */}
            {level > 0 && <StyledNodeLineTop />}

            {/* The Designation Card */}
            <Paper
                elevation={3}
                sx={{
                    p: `${nodePad}px`,
                    width: cardW,
                    minWidth: cardW,
                    maxWidth: cardW,
                    borderRadius: 2,
                    bgcolor: getNodeBgColor(),
                    borderColor: getNodeBorderColor(),
                    borderWidth: getNodeBorderWidth(),
                    borderStyle: 'solid',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                }}
            >
                {/* Dots menu at top right */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{ width: '100%', position: 'absolute', top: 4, right: 4 }}
                >
                    <IconButton size="small" onClick={handleMenuOpen}>
                        <Iconify icon="solar:menu-dots-bold" width={16} />
                    </IconButton>
                </Stack>

                {/* Level Chip */}
                <Chip
                    label={node.role === 'leader' ? 'Root Level' : `Level ${level + 1}`}
                    color={node.role === 'leader' ? 'primary' : 'default'}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }}
                />

                {/* Designation Dropdown */}
                <Box sx={{ width: '100%', px: 1 }}>
                    <Autocomplete
                        options={designations}
                        getOptionLabel={(option) => option.designation_name || ''}
                        value={node.designationId}
                        onChange={(e, v) => onDesignationChange(node.id, v)}
                        isOptionEqualToValue={(option, val) => option._id === val._id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Designation"
                                size="small"
                                variant="outlined"
                                placeholder="Choose role..."
                                sx={{
                                    '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                                    '& .MuiOutlinedInput-root': { fontSize: '0.8rem' }
                                }}
                            />
                        )}
                    />
                </Box>

                {/* Staff Assignment Indicator */}
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ width: '100%', mt: 0.5 }}>
                    <Chip
                        label={`${node.assignedUsers?.length || 0} Staff`}
                        color={node.assignedUsers?.length > 0 ? 'success' : 'warning'}
                        variant="soft"
                        size="small"
                        sx={{ height: 22, fontSize: '0.75rem', fontWeight: 'medium' }}
                    />
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        disabled={!node.designationId}
                        startIcon={<Iconify icon="solar:users-group-rounded-bold" width={14} />}
                        onClick={() => onManageStaff(node.id)}
                        sx={{ height: 24, fontSize: '0.7rem', py: 0 }}
                    >
                        Manage
                    </Button>
                </Stack>

                {/* Child Nodes Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <MenuItem onClick={handleAddChild} sx={{ fontSize: '0.875rem' }}>
                        <Iconify icon="solar:add-circle-bold" sx={{ mr: 1, color: 'primary.main' }} width={18} />
                        Add Child Designation
                    </MenuItem>
                    {node.role !== 'leader' && (
                        <>
                            <MenuItem onClick={handleRemoveNode} sx={{ color: 'warning.main', fontSize: '0.875rem' }}>
                                <Iconify icon="solar:user-cross-bold" sx={{ mr: 1 }} width={18} />
                                Remove Node (Promote children)
                            </MenuItem>
                            <MenuItem
                                onClick={handleRemoveBranch}
                                disabled={!hasChildren}
                                sx={{ color: 'error.main', fontSize: '0.875rem' }}
                            >
                                <Iconify icon="solar:trash-bin-trash-bold" sx={{ mr: 1 }} width={18} />
                                Remove Branch
                            </MenuItem>
                        </>
                    )}
                </Menu>
            </Paper>

            {/* Bottom Connector Line / Plus connector button */}
            <Box sx={{ position: 'relative', py: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 10 }}>
                <Stack alignItems="center" spacing={0}>
                    <Box sx={{ width: 2, height: 12, bgcolor: 'divider' }} />
                    <IconButton
                        onClick={handleAddChild}
                        size="small"
                        sx={{
                            color: 'primary.main',
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                            width: 28,
                            height: 28,
                            boxShadow: 1,
                            '&:hover': {
                                bgcolor: 'primary.lighter',
                                borderColor: 'primary.main',
                            },
                        }}
                    >
                        <Iconify icon="solar:add-circle-bold" width={18} />
                    </IconButton>
                    {hasChildren && <Box sx={{ width: 2, height: 12, bgcolor: 'divider' }} />}
                </Stack>
            </Box>

            {/* Render children branches recursively */}
            {hasChildren && (
                <StyledChildrenContainer spacing={Math.max(2, Math.round(8 * zoom))}>
                    {node.children.map((child, index) => (
                        <Box key={child.id} sx={{ position: 'relative' }}>
                            <StyledConnectorLine
                                first={index === 0}
                                last={index === node.children.length - 1}
                            />
                            <DesignationOrgChartNode
                                node={child}
                                designations={designations}
                                onRemoveNode={onRemoveNode}
                                onRemoveBranch={onRemoveBranch}
                                onAddChild={onAddChild}
                                onDesignationChange={onDesignationChange}
                                onManageStaff={onManageStaff}
                                level={level + 1}
                                zoom={zoom}
                            />
                        </Box>
                    ))}
                </StyledChildrenContainer>
            )}
        </StyledNodeContainer>
    );
}

DesignationOrgChartNode.propTypes = {
    node: PropTypes.object.isRequired,
    designations: PropTypes.array.isRequired,
    onRemoveNode: PropTypes.func.isRequired,
    onRemoveBranch: PropTypes.func.isRequired,
    onAddChild: PropTypes.func.isRequired,
    onDesignationChange: PropTypes.func.isRequired,
    onManageStaff: PropTypes.func.isRequired,
    level: PropTypes.number,
    zoom: PropTypes.number,
};

/**
 * Main DesignationHierarchyTree Component
 */
export default function DesignationHierarchyTree({
    tree,
    onTreeChange,
    designations,
    onManageStaff
}) {
    const [zoom, setZoom] = useState(1);
    const ZOOM_STEP = 0.1;
    const ZOOM_MIN = 0.5;
    const ZOOM_MAX = 1.5;

    // 1. Recursive Remove Branch (Entire subtree)
    const removeBranch = (currentNode, nodeIdToDelete) => {
        if (!currentNode) return null;
        if (currentNode.id === nodeIdToDelete) return null;
        if (currentNode.children) {
            currentNode.children = currentNode.children
                .map(child => removeBranch(child, nodeIdToDelete))
                .filter(child => child !== null);
        }
        return currentNode;
    };

    const handleRemoveBranch = (nodeId) => {
        const updatedTree = removeBranch({ ...tree }, nodeId);
        onTreeChange(updatedTree);
    };

    // 2. Recursive Remove Node ONLY (Promote children)
    const removeNodeOnly = (currentNode, nodeIdToDelete) => {
        if (!currentNode) return null;
        if (currentNode.id === nodeIdToDelete) return null;

        if (currentNode.children) {
            let targetIndex = -1;
            currentNode.children.forEach((child, index) => {
                if (child.id === nodeIdToDelete) targetIndex = index;
            });

            if (targetIndex !== -1) {
                const nodeToDelete = currentNode.children[targetIndex];
                const childrenToPromote = nodeToDelete.children || [];
                const newChildrenList = [...currentNode.children];
                newChildrenList.splice(targetIndex, 1, ...childrenToPromote);
                currentNode.children = newChildrenList;
                return currentNode;
            }

            currentNode.children = currentNode.children.map(child => removeNodeOnly(child, nodeIdToDelete));
        }

        return currentNode;
    };

    const handleRemoveNode = (nodeId) => {
        const updatedTree = removeNodeOnly({ ...tree }, nodeId);
        onTreeChange(updatedTree);
    };

    // 3. Recursive Designation Change
    const changeDesignationInNode = (currentNode, nodeId, newDesignation) => {
        if (!currentNode) return null;
        if (currentNode.id === nodeId) {
            currentNode.designationId = newDesignation;
            currentNode.assignedUsers = []; // Clear assigned staff on designation change
            return currentNode;
        }
        if (currentNode.children) {
            currentNode.children = currentNode.children.map(child =>
                changeDesignationInNode(child, nodeId, newDesignation)
            );
        }
        return currentNode;
    };

    const handleDesignationChange = (nodeId, newDesignation) => {
        const updatedTree = changeDesignationInNode({ ...tree }, nodeId, newDesignation);
        onTreeChange(updatedTree);
    };

    // 4. Recursive Add Child Node
    const addChildToNode = (currentNode, parentId, newChild) => {
        if (!currentNode) return currentNode;
        if (currentNode.id === parentId) {
            if (!currentNode.children) {
                currentNode.children = [];
            }
            currentNode.children.push(newChild);
            return currentNode;
        }
        if (currentNode.children) {
            currentNode.children = currentNode.children.map(child => addChildToNode(child, parentId, newChild));
        }
        return currentNode;
    };

    const handleAddChild = (parentId) => {
        const newNode = {
            id: `node-${Date.now()}`,
            designationId: null,
            assignedUsers: [],
            role: 'member',
            children: []
        };
        const updatedTree = addChildToNode({ ...tree }, parentId, newNode);
        onTreeChange(updatedTree);
    };

    if (!tree) return null;

    return (
        <Box>
            {/* Zoom Controls */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={1}
                sx={{ px: 2, py: 0.5, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tooltip title="Zoom Out">
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => setZoom((z) => Math.max(ZOOM_MIN, parseFloat((z - ZOOM_STEP).toFixed(1))))}
                            disabled={zoom <= ZOOM_MIN}
                        >
                            <Iconify icon="eva:minus-circle-outline" width={20} />
                        </IconButton>
                    </span>
                </Tooltip>

                <Slider
                    value={zoom}
                    min={ZOOM_MIN}
                    max={ZOOM_MAX}
                    step={ZOOM_STEP}
                    onChange={(_, val) => setZoom(val)}
                    sx={{ width: 100 }}
                    size="small"
                />

                <Tooltip title="Zoom In">
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => setZoom((z) => Math.min(ZOOM_MAX, parseFloat((z + ZOOM_STEP).toFixed(1))))}
                            disabled={zoom >= ZOOM_MAX}
                        >
                            <Iconify icon="eva:plus-circle-outline" width={20} />
                        </IconButton>
                    </span>
                </Tooltip>

                <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 40, textAlign: 'center' }}>
                    {Math.round(zoom * 100)}%
                </Typography>

                <Tooltip title="Reset Zoom">
                    <IconButton size="small" onClick={() => setZoom(1)}>
                        <Iconify icon="solar:restart-bold" width={18} />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* Scrollable Tree Container */}
            <Box sx={{ overflowX: 'auto', overflowY: 'auto', p: 2 }}>
                <Box sx={{ minWidth: 'fit-content' }}>
                    <DesignationOrgChartNode
                        node={tree}
                        designations={designations}
                        onRemoveNode={handleRemoveNode}
                        onRemoveBranch={handleRemoveBranch}
                        onAddChild={handleAddChild}
                        onDesignationChange={handleDesignationChange}
                        onManageStaff={onManageStaff}
                        level={0}
                        zoom={zoom}
                    />
                </Box>
            </Box>
        </Box>
    );
}

DesignationHierarchyTree.propTypes = {
    tree: PropTypes.object.isRequired,
    onTreeChange: PropTypes.func.isRequired,
    designations: PropTypes.array.isRequired,
    onManageStaff: PropTypes.func.isRequired,
};
